import React, { Component } from 'react';
/**
 * ExplanationCard — Teacher note aesthetic for explanations.
 *
 * Correct: warm amber "Waarom dit klopt" card.
 * Wrong: rose "Waarom was dat niet goed?" + amber teacher note.
 * Compact mode for inline usage.
 *
 * Co-located in practice/ domain (CL-1 s64).
 */
import { motion } from 'framer-motion';
import { Lightbulb, CheckCircle2, Info, Zap, BookOpen } from 'lucide-react';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { Card } from '../../../components/ui/Card';
import { ExplanationSteps } from './ExplanationSteps';
// ── Types ────────────────────────────────────────────────────
interface ExplanationCardProps {
  explanation: string;
  distractorExplanation?: string;
  isCorrect: boolean;
  mode?: 'full' | 'compact';
  solutionSteps?: string[];
}
// ── Component ────────────────────────────────────────────────
export function ExplanationCard({
  explanation,
  distractorExplanation,
  isCorrect,
  mode = 'full',
  solutionSteps
}: ExplanationCardProps) {
  if (mode === 'compact') {
    return <div className="bg-amber-50 p-4 rounded-xl border border-amber-200" data-id="element-11231" data-mp="26s3khj8k8k7b">
        <div className="flex items-center gap-2 font-bold text-amber-800 mb-2 text-sm" data-id="element-11232" data-mp="2brg3fswb974m">
          <Zap className="w-4 h-4" data-id="element-11233" data-mp="2gl2clb39msvo" /> Uitleg
        </div>
        <div className="text-amber-900 text-sm leading-relaxed" data-id="element-11234" data-mp="2likl50ddf635">
          <LatexRenderer content={explanation} data-id="element-11235" data-mp="2lkwz72g0cwax" />
        </div>
      </div>;
  }
  return <div className="space-y-5" data-id="element-11236" data-mp="1nwmxkok4wakc">
      {/* Wrong answer — why was it wrong? */}
      {!isCorrect && distractorExplanation && <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-11237" data-mp="1sw9bz1bp7wuq">
        
          <Card className="border-2 border-rose-100 bg-white overflow-hidden shadow-sm rounded-[2rem]" data-id="element-11238" data-mp="21sqni5nz5wr0">
            <div className="bg-rose-50 px-6 py-4 border-b border-rose-100 flex items-center gap-3" data-id="element-11239" data-mp="39arb9hsi6gu4">
              <div className="bg-white p-2 rounded-xl shadow-sm border border-rose-100 text-rose-500" data-id="element-11240" data-mp="3dbutuc1xqqz1">
                <Info className="w-5 h-5" data-id="element-11241" data-mp="1965mfpv3wk2c" />
              </div>
              <h3 className="text-lg font-black text-rose-900" data-id="element-11242" data-mp="195nfnzating6">
                Waarom was dat niet goed?
              </h3>
            </div>
            <div className="p-7 bg-rose-50/20" data-id="element-11243" data-mp="1i6tzah8rkedn">
              <ExplanationSteps text={distractorExplanation} accent="indigo" data-id="element-11244" data-mp="1i853gi64fo5a" />
            </div>
          </Card>
        </motion.div>}

      {/* Teacher note — warm amber/yellow */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: !isCorrect && distractorExplanation ? 0.1 : 0,
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-11245" data-mp="qggwkb643ttu">
        
        <div className="relative rounded-[2rem] overflow-hidden border-2 border-amber-200 shadow-md" data-id="element-11246" data-mp="zej0ozl572lc">
          <div className="h-1.5 w-full bg-amber-400" data-id="element-11247" data-mp="bof2x8umxx29" />
          <div className="bg-amber-50 px-6 py-4 border-b border-amber-100 flex items-center gap-3" data-id="element-11248" data-mp="6qx1e3iy6k4a">
            <div className="bg-white p-2 rounded-xl shadow-sm border border-amber-200" data-id="element-11249" data-mp="2ie8m0c5zuzsi">
              {isCorrect ? <CheckCircle2 className="w-5 h-5 text-emerald-500" data-id="element-11250" data-mp="2n7bgcmilid9r" /> : <Lightbulb className="w-5 h-5 text-amber-500 fill-amber-100" data-id="element-11251" data-mp="29eluci32h8cn" />}
            </div>
            <div className="flex-1" data-id="element-11252" data-mp="2ed6u6geepq0k">
              <h3 className="text-lg font-black text-amber-900" data-id="element-11253" data-mp="2ee876qezgmkx">
                {isCorrect ? 'Waarom dit klopt ✓' : 'Zo los je dit op'}
              </h3>
              <p className="text-xs text-amber-600 font-medium mt-0.5 flex items-center gap-1" data-id="element-11254" data-mp="364mhydrp9isd">
                <BookOpen className="w-3 h-3" data-id="element-11255" data-mp="2s4tr8ig6b2ke" /> Uitleg van de juf/meester
              </p>
            </div>
          </div>
          <div className="bg-amber-50/60 p-7" data-id="element-11256" data-mp="2x351uvealh3o">
            <div className="relative" data-id="element-11257" data-mp="2x23h1z7h5g8r">
              <ExplanationSteps text={explanation || 'Goed gedaan! Je hebt de juiste strategie gebruikt.'} steps={solutionSteps} accent="amber" data-id="element-11258" data-mp="1gp54ns6u05k4" />
              
            </div>
          </div>
        </div>
      </motion.div>
    </div>;
}